/**
 * Composants boutons partagés — MomsEduc Study Design System
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  AppButton  — composant universel (variant, size, loading, icon, as)   │
 * │  PrimaryButton, SecondaryButton, … — exports legacy (tw.button)        │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Usage :
 *   <AppButton variant="primary" size="lg" loading={isSending} iconRight={<ArrowRight />}>
 *     Envoyer
 *   </AppButton>
 *
 *   <AppButton variant="outline" to="/services">Voir les services</AppButton>
 *   <AppButton variant="secondary" href="https://…">Lien externe</AppButton>
 */
import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; // eslint-disable-line
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";

// ─── Design tokens ─────────────────────────────────────────────────────────
const C_PRIMARY = "#ff6c00";
const C_SECONDARY = "#111111";

// ─── Types publics ──────────────────────────────────────────────────────────

export type AppButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "danger";
export type AppButtonSize = "sm" | "md" | "lg" | "xl";

export interface AppButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Style visuel. Défaut : "primary" */
  variant?: AppButtonVariant;
  /** Taille. Défaut : "md" */
  size?: AppButtonSize;
  /** Affiche un spinner rotatif et désactive le bouton */
  loading?: boolean;
  /** Icône à gauche du texte (désactivée pendant loading) */
  iconLeft?: React.ReactNode;
  /** Icône à droite du texte (désactivée pendant loading) */
  iconRight?: React.ReactNode;
  /** Étire le bouton à 100 % de la largeur du parent */
  fullWidth?: boolean;
  /** Rend le bouton comme une balise <a> native */
  href?: string;
  /** Rend le bouton comme un <Link> React Router */
  to?: string;
}

// ─── Styles par variante ────────────────────────────────────────────────────

const variantStyles: Record<AppButtonVariant, ReturnType<typeof css>> = {
  primary: css`
    background: ${C_PRIMARY};
    color: white;
    border: 2px solid ${C_PRIMARY};
    &:hover:not(:disabled) {
      background: ${C_SECONDARY};
      border-color: ${C_SECONDARY};
    }
  `,
  secondary: css`
    background: ${C_SECONDARY};
    color: white;
    border: 2px solid ${C_SECONDARY};
    &:hover:not(:disabled) {
      background: #2a2a2a;
      border-color: #2a2a2a;
    }
  `,
  outline: css`
    background: transparent;
    color: ${C_PRIMARY};
    border: 2px solid ${C_PRIMARY};
    &:hover:not(:disabled) {
      background: ${C_PRIMARY};
      color: white;
    }
  `,
  ghost: css`
    background: transparent;
    color: ${C_PRIMARY};
    border: 2px solid transparent;
    &:hover:not(:disabled) {
      background: #fff4ec;
    }
  `,
  danger: css`
    background: #ef4444;
    color: white;
    border: 2px solid #ef4444;
    &:hover:not(:disabled) {
      background: #dc2626;
      border-color: #dc2626;
    }
  `,
};

// ─── Styles par taille ──────────────────────────────────────────────────────

const sizeStyles: Record<AppButtonSize, ReturnType<typeof css>> = {
  sm: css`
    padding: 0.5rem 1.25rem;
    font-size: 0.75rem;
  `,
  md: css`
    padding: 0.875rem 1.75rem;
    font-size: 0.85rem;
  `,
  lg: css`
    padding: 1rem 2.25rem;
    font-size: 0.9rem;
  `,
  xl: css`
    padding: 1.25rem 2.75rem;
    font-size: 1rem;
  `,
};

// ─── Base stylée ────────────────────────────────────────────────────────────

interface StyledBaseProps {
  $variant: AppButtonVariant;
  $size: AppButtonSize;
  $fullWidth: boolean;
  $loading: boolean;
}

const StyledBase = styled.button<StyledBaseProps>`
  /* Layout */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  width: ${(p) => (p.$fullWidth ? "100%" : "auto")};

  /* Typography */
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  text-decoration: none;
  white-space: nowrap;

  /* Reset */
  border: none;
  outline: none;
  cursor: ${(p) => (p.$loading ? "wait" : "pointer")};

  /* Transition */
  transition:
    background 0.25s ease,
    border-color 0.25s ease,
    color 0.25s ease,
    gap 0.25s ease,
    opacity 0.2s ease;

  /* Hover : déplacement de l'icône droite */
  &:hover:not(:disabled) .btn-icon-right {
    transform: translateX(4px);
  }
  .btn-icon-right {
    transition: transform 0.25s ease;
    display: inline-flex;
  }
  .btn-icon-left,
  .btn-icon-loader {
    display: inline-flex;
  }

  /* État désactivé */
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Focus visible (accessibilité clavier) */
  &:focus-visible {
    outline: 2px solid ${C_PRIMARY};
    outline-offset: 3px;
  }

  /* Variante */
  ${(p) => variantStyles[p.$variant]}

  /* Taille */
  ${(p) => sizeStyles[p.$size]}
`;

// ─── Spinner ────────────────────────────────────────────────────────────────

const SpinnerWrap = styled.span`
  display: inline-flex;
  animation: btn-spin 0.75s linear infinite;

  @keyframes btn-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// ─── Composant principal ────────────────────────────────────────────────────

export const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      iconLeft,
      iconRight,
      fullWidth = false,
      href,
      to,
      children,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    const baseProps = {
      $variant: variant,
      $size: size,
      $fullWidth: fullWidth,
      $loading: loading,
      disabled: isDisabled,
      ref,
      ...rest,
    };

    const content = (
      <>
        {loading ? (
          <SpinnerWrap className="btn-icon-loader" aria-hidden>
            <Loader2 size={16} />
          </SpinnerWrap>
        ) : (
          iconLeft && (
            <span className="btn-icon-left" aria-hidden>
              {iconLeft}
            </span>
          )
        )}

        {children}

        {!loading && iconRight && (
          <span className="btn-icon-right" aria-hidden>
            {iconRight}
          </span>
        )}
      </>
    );

    /* React Router Link */
    if (to) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (
        <StyledBase as={Link as any} to={to} {...baseProps}>
          {content}
        </StyledBase>
      );
    }

    /* Lien externe ou ancre */
    if (href) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (
        <StyledBase as="a" href={href} {...(baseProps as any)}>
          {content}
        </StyledBase>
      );
    }

    return <StyledBase {...baseProps}>{content}</StyledBase>;
  },
);

AppButton.displayName = "AppButton";

export default AppButton;

// ─── Legacy exports (compatibilité ascendante) ─────────────────────────────
// Ces exports sont conservés pour les composants existants qui les importent.

/** @deprecated — Utiliser <AppButton variant="primary"> */
export const PrimaryButton = tw.button`
  px-8 py-3 font-bold
  bg-primary-500 text-gray-100
  hocus:bg-secondary-500 hocus:text-gray-200
  focus:shadow-outline focus:outline-none
  transition duration-300
`;

/** @deprecated — Utiliser <AppButton variant="secondary"> */
export const SecondaryButton = tw.button`
  px-8 py-3 font-bold
  bg-secondary-500 text-gray-100
  hocus:bg-secondary-900 hocus:text-gray-200
  focus:shadow-outline focus:outline-none
  transition duration-300
`;

/** @deprecated — Utiliser <AppButton variant="outline"> */
export const OutlineButton = tw.button`
  px-8 py-3 font-bold
  bg-transparent text-primary-500
  border-2 border-primary-500
  hocus:bg-primary-500 hocus:text-gray-100
  focus:shadow-outline focus:outline-none
  transition duration-300
`;

/** @deprecated — Utiliser <AppButton variant="ghost"> */
export const GhostButton = tw.button`
  px-8 py-3 font-bold
  bg-transparent text-primary-500
  hocus:bg-primary-100 hocus:text-primary-700
  focus:shadow-outline focus:outline-none
  transition duration-300
`;

/** @deprecated — Utiliser <AppButton variant="danger"> */
export const DangerButton = tw.button`
  px-8 py-3 font-bold
  bg-red-600 text-gray-100
  hocus:bg-red-700 hocus:text-gray-200
  focus:shadow-outline focus:outline-none
  transition duration-300
`;

export const HeroPrimaryButton = tw.button`
  px-8 py-3 mt-10
  text-sm sm:text-base sm:mt-16 sm:px-8 sm:py-4
  bg-primary-500 text-gray-100 font-bold
  shadow transition duration-300
  hocus:bg-gray-100 hocus:text-primary-500
  focus:shadow-outline
`;

export const HeroSecondaryButton = tw.button`
  px-8 py-3 mt-10
  text-sm sm:text-base sm:mt-16 sm:px-8 sm:py-4
  bg-gray-100 text-primary-500 font-bold
  shadow transition duration-300
  hocus:bg-secondary-500 hocus:text-gray-100
  focus:shadow-outline
`;

/** Alias nommé pour accès uniforme */
export const Button = AppButton;
